import { HttpParams } from '@angular/common/http';
import { forwardRef, Inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TICKET_ATTACHMENTS, TICKET_BY_ID, TICKET_NOTES, TICKET_TYPES, TICKETS, TICKETS_V2 } from '../common/endpoints';
import { PagedList, Ticket, TicketItemAttachment, TicketItemNote, TicketType } from '../model';
import { HttpUtility } from '../utility';
import { AuthenticationService } from './authentication.service';
import { HttpService } from './http.service';

@Injectable()
export class TicketService {


    constructor(
        @Inject(forwardRef(() => HttpService)) private httpService: HttpService,
        @Inject(forwardRef(() => HttpUtility)) private httpUtility: HttpUtility,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService
    ) { }

    getTickets(page: number, size: number, sort: string[], received: boolean, searchText: string, statuses: string[], thingId?: string, eventId?: string): Promise<PagedList<Ticket>> {
        let params = new HttpParams();
        params = params.set('page', page + '');
        params = params.set('size', size + '');
        if (sort && sort[0]) {
            params = params.set('sort', sort[0] + ',' + sort[1]);
        }
        params = params.set('received', received);
        if (searchText) {
            params = params.set('searchText', `*${searchText}*`);
        }
        if (statuses?.length) {
            statuses.forEach(s => params = params.append('status', s));
        }
        if (thingId) {
            params = params.set('thingId', thingId);
        }
        if (eventId) {
            params = params.set('eventId', eventId);
        }
        return firstValueFrom(this.httpService.get<PagedList<Ticket>>(TICKETS_V2, params));
    }

    getRecursivelyAllTickets(received: boolean, thingId?: string, eventId?: string, page?: number, tickets?: Ticket[]): Promise<Ticket[]> {
        if (!page) {
            page = 0;
        }
        if (!tickets) {
            tickets = [];
        }
        return this.getTickets(page, 200, ['code', 'asc'], received, null, null, thingId, eventId)
            .then(pagedTickets => {
                tickets = tickets.concat(pagedTickets.content);
                if (pagedTickets.last) {
                    return tickets;
                } else {
                    return this.getRecursivelyAllTickets(received, thingId, eventId, page, tickets);
                }
            });
    }


    getById(id: string): Promise<Ticket> {
        return firstValueFrom(this.httpService.get<Ticket>(TICKET_BY_ID.replace('{id}', id)));
    }

    getTicketTypes(): Promise<TicketType[]> {
        return firstValueFrom(this.httpService.get<TicketType[]>(TICKET_TYPES));
    }

    save(body: any): Promise<Ticket> {
        return firstValueFrom(this.httpService.post<Ticket>(TICKETS, body));
    }

    patch(id: string, body: any): Promise<Ticket> {
        return firstValueFrom(this.httpService.patch<Ticket>(TICKET_BY_ID.replace('{id}', id), body));
    }

    saveNote(id: string, body: TicketItemNote): Promise<void> {
        return firstValueFrom(this.httpService.post<void>(TICKET_NOTES.replace('{id}', id), body));
    }

    uploadAttachment(id: string, data: FormData): Promise<void> {
        return firstValueFrom(this.httpService.post<void>(TICKET_ATTACHMENTS.replace('{id}', id), data));
    }

    downloadAttachment(attachment: TicketItemAttachment): void {
        firstValueFrom(this.httpService.externalGet(attachment.url, { responseType: 'blob' })).then(blob => {
            this.httpUtility.wrapFileAndDownload({ file: blob, fileName: attachment.filename });
        });
    }

    canReceiveTickets(): boolean {
        if (this.authenticationService.isCustomerUser()) {
            return false;
        }
        if (this.authenticationService.isPartnerUser()) {
            return this.authenticationService.getUser().partner.serviceProvider;
        }
        if (this.authenticationService.isOrganizationUser()) {
            const org = this.authenticationService.getUser().organization;
            return org.serviceProvider || !org.parent;
        }
        return false;
    }
}